import type {
  MfgMissingCostMaterial,
  MfgPricingLayerColumn,
  MfgPricingLayerGridRow,
} from "./cogsTypes";
import {
  bandRoundUpFromColumns,
  evaluatePricingLayers,
  type PricingLayerColumnDef,
} from "./pricingLayerEvaluate.js";
import { isOverheadCogsColumn } from "./overheadCogsLink";

export type CogsGap = "materials" | "labor" | "other";
export type CogsGapMatch = "and" | "or";

export const COGS_RECIPE_KINDS = ["product", "option", "package"] as const;
export type CogsRecipeKind = (typeof COGS_RECIPE_KINDS)[number];

/** Products, or options and packages together. Empty means every recipe kind. */
export type CogsSkuScope = "products" | "addons" | "all";

export function recipeKindsForScope(scope: CogsSkuScope): readonly CogsRecipeKind[] {
  if (scope === "products") return ["product"];
  if (scope === "addons") return ["option", "package"];
  return COGS_RECIPE_KINDS;
}

export type CogsCatalogFacetFilters = {
  series: readonly string[];
  roof_style: readonly string[];
  size_band: readonly string[];
  gaps?: readonly CogsGap[];
  gapMatch?: CogsGapMatch;
  recipeKinds?: readonly CogsRecipeKind[];
};

function recipeKindOf(row: MfgPricingLayerGridRow): CogsRecipeKind {
  if (row.recipe_kind === "option" || row.recipe_kind === "package" || row.recipe_kind === "product") {
    return row.recipe_kind;
  }
  return "product";
}

/** A product has this gap when the saved COGS row still cannot price that piece. */
export function cogsRowHasGap(row: MfgPricingLayerGridRow, gap: CogsGap): boolean {
  if (gap === "materials") return (row.materials_unpriced_leaf_count ?? 0) > 0;
  if (gap === "labor") return (row.labor_rate_skipped ?? 0) > 0;
  return (
    row.materials_cost_usd == null ||
    Boolean(row.materials_error_text?.trim()) ||
    Boolean(row.materials_is_stale) ||
    (row.materials_zero_on_hand_leaf_count ?? 0) > 0
  );
}

function matchesGaps(
  row: MfgPricingLayerGridRow,
  gaps: readonly CogsGap[],
  match: CogsGapMatch,
): boolean {
  if (gaps.length === 0) return true;
  if (row.subject_kind !== "recipe") return false;
  const hits = gaps.map((gap) => cogsRowHasGap(row, gap));
  return match === "or" ? hits.some(Boolean) : hits.every(Boolean);
}

export type CogsMaterialsPatch = {
  recipe_id: string;
  materials_cost_usd: number;
  is_stale: boolean;
  computed_at: string | null;
  error_text?: string;
  unpriced_leaf_count?: number;
  zero_on_hand_leaf_count?: number;
  missing_cost_materials?: MfgMissingCostMaterial[];
};

function has(selected: readonly string[], value: string): boolean {
  return selected.length === 0 || selected.includes(value);
}

/** Group, style, size, and missing-data gaps apply to product rows. Uses and variants stay unless a gap is selected. */
export function filterCogsCatalogRows(
  rows: readonly MfgPricingLayerGridRow[],
  filters: CogsCatalogFacetFilters,
): MfgPricingLayerGridRow[] {
  const gaps = filters.gaps ?? [];
  const gapMatch = filters.gapMatch ?? "and";
  return rows.filter((row) => {
    if (!matchesGaps(row, gaps, gapMatch)) return false;
    if (row.subject_kind !== "recipe") return true;
    const kinds = filters.recipeKinds ?? [];
    if (kinds.length > 0 && !kinds.includes(recipeKindOf(row))) return false;
    return (
      has(filters.series, row.series) &&
      has(filters.roof_style, row.roof_style) &&
      has(filters.size_band, row.size_band)
    );
  });
}

function columnDefs(columns: readonly MfgPricingLayerColumn[]): PricingLayerColumnDef[] {
  return columns.filter((column) => column.is_active).map((column) => ({
    id: column.id,
    band: column.band,
    key: column.key,
    label: column.label,
    op: column.op,
    source_kind: column.source_kind,
    source_column_id: column.source_column_id,
    source_band: column.source_band,
    sort_order: column.sort_order,
    is_active: column.is_active,
    ...(column.metadata?.rate_kind === "markup" ? { rate_kind: "markup" as const } : {}),
  }));
}

function isModeledLabor(row: MfgPricingLayerGridRow, column: MfgPricingLayerColumn): boolean {
  return (
    (column.key === "labor" || column.source_module === "labor") &&
    ((row.labor_rate_count ?? 0) > 0 || (row.labor_rate_skipped ?? 0) > 0)
  );
}

function reprice(
  row: MfgPricingLayerGridRow,
  columns: readonly MfgPricingLayerColumn[],
  values: Record<string, number | null>,
  materials: number,
): MfgPricingLayerGridRow {
  const evaluated = evaluatePricingLayers({
    materials_cost_usd: materials,
    columns: columnDefs(columns),
    values_by_column_id: values,
    round_up: bandRoundUpFromColumns(columns),
  });
  return {
    ...row,
    values_by_column_id: values,
    column_results: evaluated.column_results,
    cogs_total_usd: evaluated.cogs_total_usd,
    wholesale_total_usd: evaluated.wholesale_total_usd,
    msrp_total_usd: evaluated.msrp_total_usd,
    dynamic_wholesale_usd: evaluated.wholesale_total_usd,
    dynamic_msrp_usd: evaluated.msrp_total_usd,
  };
}

/** Null when this SKU's labor is the matched-rate sum and must not be overwritten. */
export function cogsRowWithColumnValue(
  row: MfgPricingLayerGridRow,
  columns: readonly MfgPricingLayerColumn[],
  columnId: string,
  value: number,
): MfgPricingLayerGridRow | null {
  const column = columns.find((item) => item.id === columnId);
  if (!column || isModeledLabor(row, column) || isOverheadCogsColumn(column)) return null;
  return reprice(
    row,
    columns,
    { ...row.values_by_column_id, [columnId]: value },
    row.materials_cost_usd ?? 0,
  );
}

export function cogsRowsWithBandRoundUp(
  rows: readonly MfgPricingLayerGridRow[],
  columns: readonly MfgPricingLayerColumn[],
): MfgPricingLayerGridRow[] {
  return rows.map((row) => reprice(row, columns, row.values_by_column_id, row.materials_cost_usd ?? 0));
}

export function cogsRowWithMaterialsSummary(
  row: MfgPricingLayerGridRow,
  summary: CogsMaterialsPatch,
  columns: readonly MfgPricingLayerColumn[],
): MfgPricingLayerGridRow {
  const failed = Boolean(summary.error_text) || summary.computed_at == null;
  const materials = failed ? 0 : summary.materials_cost_usd;
  const priced = reprice(row, columns, row.values_by_column_id, materials);
  return {
    ...priced,
    materials_cost_usd: failed ? null : summary.materials_cost_usd,
    materials_is_stale: summary.is_stale,
    materials_computed_at: summary.computed_at,
    materials_unpriced_leaf_count: summary.unpriced_leaf_count ?? row.materials_unpriced_leaf_count,
    materials_zero_on_hand_leaf_count:
      summary.zero_on_hand_leaf_count ?? row.materials_zero_on_hand_leaf_count,
    materials_missing_costs: summary.missing_cost_materials ?? row.materials_missing_costs,
    materials_error_text: summary.error_text ?? "",
  };
}
